 // hooks/useImageUpload.js
import { useState } from 'react';
import { auth } from '../../firebase';

export const useImageUpload = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);

  const appendFormValue = (formData, key, value) => {
    if (value === undefined || value === null || value === '') return;
    if (typeof value === 'object') {
      formData.append(key, JSON.stringify(value));
      return;
    }
    formData.append(key, String(value));
  };

  /**
   * Upload image via Cloud Function to access restricted paths
   * @param {File} file - The file to upload
   * @param {Object} metadata - Metadata for the itinerary
   * @param {Object} options - Additional upload options
   * @returns {Promise<string>} - A promise that resolves to the download URL
   */
  const uploadImage = async (file, metadata = {}, options = {}) => {
    if (!file) return null;
    setIsUploading(true);
    setError(null);
    setProgress(0);
    
    try {
      const hasSizeLimit = Number.isFinite(options.maxSizeKB) && options.maxSizeKB > 0;
      const maxSizeKB = hasSizeLimit ? options.maxSizeKB : 400;
      const maxSizeInBytes = maxSizeKB * 1024;
      if (hasSizeLimit && file.size > maxSizeInBytes) {
        throw new Error(`File size exceeds ${maxSizeKB}KB limit. Your file is ${(file.size / 1024).toFixed(1)}KB.`);
      }
      
      // Create a FormData object to send the file
      const formData = new FormData();
      formData.append('image', file);

      // Add metadata to the request
      Object.entries(metadata || {}).forEach(([key, value]) => {
        appendFormValue(formData, key, value);
      });

      // Add optional custom storage path
      if (options.storagePath) formData.append('storagePath', options.storagePath);

      if (hasSizeLimit) {
        formData.append('maxSizeKB', maxSizeKB);
      } else {
        formData.append('skipSizeValidation', 'true');
      }
      
      // Set progress to intermediate states
      setProgress(10);
      
      // Send the upload request to our cloud function
      let authToken = null;
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          throw new Error('You must be logged in to upload images.');
        }
        authToken = await currentUser.getIdToken();
      } catch (tokenError) {
        const authenticationError = new Error(
          tokenError.message || 'Could not resolve the Firebase authentication token for image upload.'
        );
        authenticationError.code = tokenError.code || 'IMAGE_UPLOAD_AUTH_FAILED';
        throw authenticationError;
      }

      const response = await fetch('https://us-central1-guidesofia-access-control.cloudfunctions.net/backendImageUploaderToFirebase', {
        method: 'POST',
        headers: authToken ? {
          Authorization: `Bearer ${authToken}`,
        } : undefined,
        body: formData
      });
      
      setProgress(90);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || 'Failed to upload image');
      }
      
      const data = await response.json();
      setProgress(100);
      
      return data.url; // Return the public URL
    } catch (error) {
      const isNetworkFailure = error instanceof TypeError && error.message === 'Failed to fetch';
      const resolvedError = isNetworkFailure
        ? new Error(`Cannot reach the image upload backend from ${window.location.origin}. Check the Firebase Functions CORS configuration and network connection.`)
        : error;
      if (isNetworkFailure) resolvedError.code = 'IMAGE_UPLOAD_NETWORK_ERROR';
      console.error('Error uploading image:', resolvedError);
      setError(resolvedError.message || 'Failed to upload image');
      throw resolvedError;
    } finally {
      setIsUploading(false);
    }
  };

  return { 
    uploadImage,
    isUploading, 
    error,
    progress
  };
};
